import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  isLoading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
}

const variants: Record<ButtonVariant, string> = {
  primary: `
    bg-brand-gradient
    text-brand-darkNavy font-bold
    shadow-lg shadow-brand-yellow/25 
    hover:shadow-xl hover:shadow-brand-yellow/40
    border border-brand-yellow/30
  `,
  secondary: `
    bg-brand-blue/80 hover:bg-brand-lightBlue/80 
    text-white font-semibold
    border border-brand-lightBlue hover:border-brand-yellow/50
    backdrop-blur-sm
  `,
  outline: `
    bg-transparent 
    hover:bg-brand-yellow/10 
    text-brand-yellow hover:text-brand-yellow
    border-2 border-brand-yellow hover:border-brand-yellow
  `,
  ghost: `
    bg-transparent 
    hover:bg-brand-blue/50 
    text-dark-300 hover:text-white
    border border-transparent hover:border-brand-lightBlue/50
  `,
};

const sizes: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm gap-1.5 rounded-full',
  md: 'px-6 py-3 text-base gap-2 rounded-full',
  lg: 'px-8 py-4 text-lg gap-2 rounded-full',
  xl: 'px-10 py-5 text-xl gap-3 rounded-full',
};

const iconSizes: Record<ButtonSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-7 h-7',
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  isLoading,
  fullWidth,
  children,
  className = '',
  disabled,
  onClick,
  type = 'button',
}: ButtonProps) {
  const isDisabled = disabled || isLoading;

  return (
    <motion.button
      whileTap={isDisabled ? {} : { scale: 0.97 }}
      whileHover={isDisabled ? {} : { scale: 1.02 }}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      className={`
        relative inline-flex items-center justify-center
        transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      disabled={isDisabled}
      onClick={onClick}
      type={type}
    >
      {/* Shine effect */}
      {variant === 'primary' && !isDisabled && (
        <motion.div
          className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
          initial={false}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12"
            initial={{ x: '-200%' }}
            whileHover={{ x: '200%' }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
          />
        </motion.div>
      )}

      {isLoading ? (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
          className={`border-2 border-current border-t-transparent rounded-full ${iconSizes[size]}`}
        />
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <span className={`shrink-0 ${iconSizes[size]}`}>{icon}</span>
          )}
          {children}
          {icon && iconPosition === 'right' && (
            <span className={`shrink-0 ${iconSizes[size]}`}>{icon}</span>
          )}
        </>
      )}
    </motion.button>
  );
}
